import { SeatType, VesselRole } from '@/store/people/people.types';
import type { Person, PersonService } from '@/store/people/people.types';
import {
  PaymentStatus,
  SubscriptionTier,
  seatTypeFor,
} from '@/store/subscriptions/subscriptions.types';
import type { SeatPool, Subscription } from '@/store/subscriptions/subscriptions.types';
import type { Vessel } from '@/store/vessels/vessels.types';

import { canManageSeats, membershipOf } from './subscription.access';
import { isLiveSubscription, isPaidPlan } from './subscription.display';

/**
 * The seat model behind the vessel detail view: which seats a subscription pays for, who
 * sits in them, and who may be put in one.
 *
 * The pools themselves and the `seat_type` each one means on each tier are the server's
 * contract, so they live beside the subscription types; both are re-exported here, so page
 * code reads the whole seat vocabulary from one place.
 */

export type { SeatPool };
export { seatTypeFor };

export const SEAT_POOLS: readonly SeatPool[] = ['management', 'crew'];

export const SEAT_POOL_LABEL: Record<SeatPool, string> = {
  management: 'Management',
  crew: 'Crew',
};

/** The pool a paid seat fills, whichever tier it names. */
const PAID_SEAT_POOL: Partial<Record<SeatType, SeatPool>> = {
  [SeatType.NAVIGATE_SEAT]: 'management',
  [SeatType.NAVIGATE_CREW]: 'crew',
  [SeatType.COMMAND_SEAT]: 'management',
  [SeatType.COMMAND_CREW]: 'crew',
};

/**
 * Whether someone holding `held` sits in `pool`, sold on this tier as `seatType`. A paid seat
 * counts by its pool, not its tier: a tier change commits the new plan before the server
 * rewrites anyone's seat (`reseatAfterPlanChange` runs in its own transaction after the
 * webhook's), so people read in between still hold the old tier's seat — in the same pool.
 */
export const sitsIn = (held: SeatType | null, seatType: SeatType, pool: SeatPool): boolean =>
  held === seatType || (isPaidSeat(seatType) && !!held && PAID_SEAT_POOL[held] === pool);

/**
 * How many seats the pool holds. Zero unless the subscription is one the server treats as
 * live — a dead row still carries seat counts, but nothing behind it was ever provisioned.
 */
export const seatCapacity = (subscription: Subscription | null, pool: SeatPool): number => {
  if (!isLiveSubscription(subscription) || !seatTypeFor(subscription.tier, pool)) return 0;
  return pool === 'management' ? subscription.seats : subscription.crewSeats;
};

/** A plan in good standing — the only kind a seat is added to. */
const IN_GOOD_STANDING = new Set<PaymentStatus | null>([
  PaymentStatus.ACTIVE,
  PaymentStatus.TRIALING,
]);

/**
 * What a full pool offers instead of a free seat: one more seat bought onto the plan, or —
 * for management on single-user Navigate, whose count the API pins at 1 — a move up to
 * Command. `null` when neither is on the table: no paid plan, or one that is ending or
 * behind on payment, which is no place for a new charge.
 */
export const seatOffer = (
  subscription: Subscription | null,
  pool: SeatPool,
): 'buy' | 'upgrade' | null => {
  if (
    !isPaidPlan(subscription) ||
    !IN_GOOD_STANDING.has(subscription.paymentStatus) ||
    subscription.cancelAtPeriodEnd ||
    !subscription.interval ||
    !seatTypeFor(subscription.tier, pool)
  ) {
    return null;
  }
  return pool === 'management' && subscription.tier === SubscriptionTier.NAVIGATE
    ? 'upgrade'
    : 'buy';
};

/**
 * How many of the pool's seats are spoken for by an owner who has not come aboard yet — one,
 * or none.
 *
 * A vessel with no owner still has a reserved management seat, and the captain paying for it
 * said at checkout whether it is theirs or the owner's to come. Said the owner's, the seat is
 * nobody's until they join: `seatForPayingCaptain` (the People service's
 * `internal/app/payments/captain.go`) counts it as taken while no person holds it, and
 * `AssignSeat` would hand it to whoever is offered it, which is what holding it is meant to
 * prevent. So it is drawn, and it is not offered.
 *
 * Only ever the management pool, and only on a paid plan: harbor's seat is the free default
 * everybody already holds, and nothing is reserved out of it.
 */
export const seatsHeldForOwner = (
  { vessel, subscription }: { vessel: Vessel; subscription: Subscription | null },
  pool: SeatPool,
): number =>
  pool === 'management' &&
  !vessel.ownerId &&
  isPaidPlan(subscription) &&
  subscription.captainWillAddOwner
    ? 1
    : 0;

/**
 * Who may hold a seat, by role. The server enforces none of this — `AssignSeat` checks only
 * the caller's rights, the tier match and the remaining capacity — so this is the whole of
 * the rule and it lives here.
 *
 * Guests are never seated: they are temporary by design and a cron sweeps them off the
 * vessel once their event ends. Vendors (the service-provider role) are excluded from the
 * management pool but may take a crew seat.
 */
const INELIGIBLE_ROLES: Record<SeatPool, readonly string[]> = {
  management: [VesselRole.GUEST, VesselRole.VENDOR],
  crew: [VesselRole.GUEST],
};

export const canOccupy = (role: string, pool: SeatPool): boolean =>
  !!role && !INELIGIBLE_ROLES[pool].includes(role);

/**
 * Whether this is a seat somebody paid for. `harbor_seat` is the free default every service
 * grants and what `DELETE /roles/seat` resets a freed seat to, so holding one amounts to
 * holding nothing: it neither blocks the person from being given a paid seat nor is worth
 * offering to empty.
 */
export const isPaidSeat = (seatType: SeatType | null): boolean =>
  !!seatType && seatType !== SeatType.HARBOR_SEAT;

// ---------------------------------------------------------------------------
// Occupancy
// ---------------------------------------------------------------------------

/** A person as a seat shows them: a face, a name to fall back on, and their role. */
export interface SeatPerson {
  personId: string;
  firstName: string;
  lastName: string;
  avatar: string | null;
  role: string;
  /** The seat they hold today — `null` for nobody, or the other pool's when reassigning. */
  seatType: SeatType | null;
}

export const toSeatPerson = (person: Person, membership: PersonService): SeatPerson => ({
  personId: person.id,
  firstName: person.personInfo.firstName,
  lastName: person.personInfo.lastName,
  avatar: person.avatar || null,
  role: membership.role,
  seatType: membership.seatType,
});

/** Everyone with a role on this vessel, flattened to what the seat rows need. */
export const seatPeopleOf = (people: Person[], serviceId: string): SeatPerson[] =>
  people.flatMap((person) => {
    const membership = membershipOf(person, serviceId);
    return membership ? [toSeatPerson(person, membership)] : [];
  });

export const displayName = ({ firstName, lastName }: SeatPerson): string =>
  [firstName, lastName].filter(Boolean).join(' ') || 'Unnamed';

/** Roles read as `first_officer` on the wire; shown as words. */
export const roleLabel = (role: string): string =>
  role ? role.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()) : 'No role';

/**
 * Whether this occupant's seat may be emptied.
 *
 * Two seats are never a target. The owner's, which the server refuses outright (403, `The
 * owner's seat cannot be removed`) — it comes with the vessel. And the viewer's own, which
 * the server would happily empty and which they could then not get back: freeing a seat
 * resets it to `harbor_seat`, and `SeatManagement` clears the seat allow-list only on
 * `navigate_seat` and `command_seat` (`1785915993_add_seat_management_to_seats`), so
 * emptying your own management seat is what takes away the right you would need to fill it
 * again. On a vessel with no owner and one captain there is then nobody left who can seat
 * anyone, and the vessel's paid seats are stranded.
 *
 * It costs nothing to forbid: handing your own seat to somebody else was never possible in
 * this order anyway — you lose the right to assign the moment you vacate — and the only
 * people who ever see these controls are the owner and the captain, since the crew seats
 * carry no `SeatManagement` at all.
 */
export const canVacate = (person: SeatPerson, viewerPersonId: string | undefined): boolean =>
  person.role !== VesselRole.OWNER && person.personId !== viewerPersonId;

/**
 * The owner leads the row: theirs is the one seat that came with the vessel and the one that
 * cannot be emptied, so it reads as the anchor the rest of the pool hangs off. Everyone else
 * keeps the order the API returned them in — `sort` is stable.
 */
export const byOwnerFirst = (a: SeatPerson, b: SeatPerson): number =>
  Number(b.role === VesselRole.OWNER) - Number(a.role === VesselRole.OWNER);

/**
 * Whether the signed-in person may assign seats on this vessel at all.
 *
 * The right, not the role: `PATCH|DELETE /roles/seat` are gated on `SeatManagement`
 * (`server/server.go`), which the owner and the captain hold by default — whoever pays for
 * the seats hands them out — and which an owner may withdraw from a role per vessel.
 */
export const canAssignSeats = (me: Person | undefined, serviceId: string): boolean =>
  canManageSeats(membershipOf(me, serviceId));
